/**
 * Pure utility functions for peripheral association/dissociation.
 */

/**
 * Checks which items from lastList are not present in newList (i.e., were removed/dissociated).
 */
export function checkDisassociatedPeripheral(
    lastList: any[],
    newList: any[]
): any[] {
    const res: any[] = [];
    for (let index = 0; index < lastList.length; index++) {
        const e = lastList[index];
        if (newList.indexOf(e) === -1 && e !== undefined) {
            res.push(e);
        }
    }
    return res;
}

/**
 * Gets an array of non-empty application rows from selectedRows.
 */
export function getArrayOfRows(
    selectedRows: any[],
    applicationLabel: string
): any[] {
    let arrayOfRows: any[] = [];
    selectedRows.forEach((elt: any) => {
        if (elt[applicationLabel] !== "") {
            arrayOfRows.push(elt);
        }
    });
    return [...new Set(arrayOfRows)];
}

/**
 * Builds a printer object for association.
 * When isFromSelectedRows is true, builds default ticket/general/divers config using salesModes.
 * When false, preserves existing config from the element.
 */
export function buildPrinterObject(
    el: any,
    isFromSelectedRows: boolean,
    salesModes: any[],
    t: (key: string) => string
): any {
    if (isFromSelectedRows) {
        return {
            id_printer: el["Id"],
            designation: el[t("Printer name")],
            mark: el[t("Mark")],
            port: el["port"],
            note: el[t("Remark")],
            status: "in line",
            state: "to configure",
            path: el["path"],
            font_size: el[t("Font size")],
            edit_date: "",
            type: el["type"],
            add_date: "",
            speed: el["speed"],
            ip: el["ip"],
            port_series: el["port_series"],
            ticket_width: el["ticket_width"],
            tickets: el["tickets"],
            code: el["code"],
            general: {
                automatic_printing: {
                    NumberCustomer: 1,
                    NumberTicketsKitchen: 1,
                },
                information_mode: {
                    information_bold: false,
                    printer_information_mode: false,
                },
                ticket_header: {
                    logo_customer: false,
                    logo_kitchen: false,
                    name_phone_number_bold: false,
                    Path_customer: "",
                    Path_kitchen: "",
                    sale_mode_black_backround: false,
                    sale_mode_bold: true,
                },
            },
            customer_ticket: {
                customerTicketSalesModes:
                    el["tickets"].value === "01" ||
                    el["tickets"].value === 1
                        ? salesModes.map((sm: any) => ({
                              designation: sm.designation,
                              state: true,
                          }))
                        : salesModes,
                dateTimeDeliveryModeSale: salesModes,
                print_date_time_delivery_mode_sale: {
                    provider_name: false,
                    without_price:
                        el["tickets"].value === "01" ||
                        el["tickets"].value === 1,
                    print_notes:
                        el["tickets"].value === "01" ||
                        el["tickets"].value === 1,
                    items_lines: false,
                    method_payment: false,
                    method_payment_TVA: false,
                },
            },
            Kitchen_ticket: {
                note_kitchen_ticket: {
                    print_note:
                        el["tickets"].value === "02" ||
                        el["tickets"].value === "03" ||
                        el["tickets"].value === "04",
                },
                ticketKitchenModeSale:
                    el["tickets"].value === "02" ||
                    el["tickets"].value === "03" ||
                    el["tickets"].value === "04"
                        ? salesModes.map((sm: any) => ({
                              designation: sm.designation,
                              state: true,
                          }))
                        : salesModes,
                customerAddressModeSale: salesModes,
                customer_information_mode_sale: {
                    customerInformationModeSaleName: salesModes,
                    customerInformationModeSaleNumTel: salesModes,
                    customerInformationModeSaleCity: salesModes,
                    customerInformationModeSaleAdress: salesModes,
                    customerInformationModeSaleCode: salesModes,
                    customerInformationModeSaleCompany: salesModes,
                },
            },
            modelKitchenTicket: {
                model_1: {
                    black_background: true,
                    isChecked: true,
                    smaller_font: true,
                },
                model_2: {
                    isChecked: false,
                    menu_component: false,
                    name_menu: false,
                    online_order: false,
                    single_label: false,
                },
                model_3: {
                    Activate_manual_dispatch_follow: false,
                    dispatch_automatically_follow: false,
                    follow_kitchen_ticket: false,
                    font_small: false,
                    isChecked: false,
                    order_kitchen: false,
                    Send_changes_kitchen: false,
                    ticket_additions_cancellations: false,
                },
                model_4: { isChecked: false },
                model_5: { isChecked: false },
            },
            divers: {
                Code: {
                    barcode_ticket: "",
                    checked_barcode_ticket: false,
                    checked_QR_code: false,
                    QR_code: "",
                },
                paperless_ticket: { paperless_ticket_custom: true },
                printing_options: {
                    force_paper: false,
                    special_characters: false,
                },
            },
            isArchived:
                el["isArchived"] !== undefined ? el["isArchived"] : false,
        };
    } else {
        return {
            id_printer: el["Id"],
            designation:
                el.designation !== undefined
                    ? el.designation
                    : el[t("Printer name")],
            mark: el[t("Mark")],
            port: el["port"],
            note: el[t("Remark")],
            status: "in line",
            state: "to configure",
            path: el["path"],
            font_size: el[t("Font size")],
            edit_date: "",
            type: el["type"],
            add_date: "",
            speed: el["speed"],
            ip: el["ip"],
            port_series: el["port_series"],
            ticket_width: el["ticket_width"],
            tickets: el["tickets"],
            code: el["code"],
            general: el["general"],
            customer_ticket: el["customer_ticket"],
            Kitchen_ticket: el["Kitchen_ticket"],
            modelKitchenTicket: el["modelKitchenTicket"],
            divers: el["divers"],
            isArchived:
                el["isArchived"] !== undefined ? el["isArchived"] : false,
        };
    }
}

/**
 * Builds a monetic object for association.
 */
export function buildMoneticObject(
    el: any,
    t: (key: string) => string
): any {
    return {
        id_monetic: el["Id"],
        designation: el[t("Designation")],
        mark: el[t("Mark")],
        port: el["port"],
        note: el[t("Remark")],
        status: "in line",
        state: "to configure",
        path: el["url"],
        android: el["android"],
        merchant_code: el["merchant_code"],
        tpe_name: el["tpe_name"],
        device_code: el["device_code"],
        speed: el["speed"],
        ip: el["ip"],
        payment_type: el["payment_type"],
        kiss: el["kiss"],
        reference: el[t("Reference")],
        general: {},
        terminal_type: el["terminal_type"],
        setup_type: el["setup_type"],
        timeout: el["timeout"],
        payment_method: el["payment_method"],
        webhook: el.webhook,
        adyenIp: el.adyenIpAddress ?? el.adyenIp,
        mode: el.mode,
        idIntegrator: el.idIntegrator,
        autoValidate: el.autoValidate,
        currency: el.currency,
        adyenConfig: el.adyenConfig,
        key: el.key,
        serialNumber: el.serialNumber,
        communication: el.communication,
    };
}

/**
 * Builds a cash management object for association.
 */
export function buildCashManagementObject(
    el: any,
    t: (key: string) => string
): any {
    return {
        id_cash_management: el["Id"],
        designation: el[t("Designation")],
        mark: el[t("Mark")],
        port_number: el["port_number"],
        reference: el[t("Reference")],
        note: el[t("Remark")],
        server_directory: el["server_directory"],
        activate: "",
        user: el["user"],
        ip: el["ip"],
        password: el["password"],
        pos_id: el["pos_id"],
        coin_loading_alert: {},
        empty_coin_alert: {},
        coin_status: {},
        collection_alert: {},
        withdrawal_Reason: {},
        maintenance: {},
        cash_Fund: {},
        accepted_denomination: {
            "5.00 €": false,
            "10.00 €": false,
            "20.00 €": false,
            "50.00 €": false,
            "100.00 €": false,
            "200.00 €": false,
            "500.00 €": false,
        },
    };
}
